import React, { useEffect } from 'react';
import { cssColorValue, semanticColors, spacer16, spacer20, spacer40, Text } from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';
import UserProfile from 'components/Greetings/UserProfile';
import SkeletonLoading from 'components/SkeletonLoading';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';
import { useCustomer } from 'utils/user';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';

const Greeting = styled.div`
  margin: ${spacer20} 0;
  height: 48px;
`;

const LoginSuggestionType = styled(Text)`
  a {
    color: ${cssColorValue(semanticColors.textBase)};
  }
`;

const getLoginUrl = () => {
  const loginUrl = 'https://accounts.spotify.com/login/';
  return `${loginUrl}?continue=${encodeURIComponent(window.location.href)}`;
};

interface GreetingsProps {
  ubi: UbiHomePage;
}

const GreetingsContent = ({ ubi }: GreetingsProps) => {
  const { traw } = useTranslation();
  const { isFetching, user } = useCustomer();
  const suggestToLogin = !(isFetching || user);

  const {
    verticalConfig: {
      features: { disableAuthenticatedFeatures },
    },
  } = useServerSideConstants();

  useEffect(() => {
    if (suggestToLogin && !disableAuthenticatedFeatures) {
      ubi.logLoginButtonImpression();
    }
  }, [suggestToLogin, disableAuthenticatedFeatures, ubi]);

  if (suggestToLogin && disableAuthenticatedFeatures) {
    return null;
  }

  return (
    <Greeting>
      {user && <UserProfile user={user} />}
      {isFetching && <SkeletonLoading width={200} height={48} />}
      {suggestToLogin && (
        <LoginSuggestionType variant="titleSmall" semanticColor="textSubdued">
          {traw('home_login_prompt', { url: getLoginUrl() }, event => {
            const target = event.target as Element;
            if (target.nodeName.toLowerCase() === 'a') {
              ubi.logLoginButtonHit(getLoginUrl());
            }
          })}
        </LoginSuggestionType>
      )}
    </Greeting>
  );
};

const Greetings = ({ ubi }: GreetingsProps) => {
  const { t } = useTranslation();
  const { isPlain } = useServerSideConstants();

  return (
    <>
      <Text as="h1" variant="bodyMedium" semanticColor="textSubdued" paddingBottom={spacer16}>
        {t('spotify_support')}
      </Text>

      <Text as="h2" variant="titleLarge" paddingBottom={spacer40}>
        {/* Change key of translation since it's part of a new context */}
        {t('search_title')}
      </Text>
      {!isPlain && <GreetingsContent ubi={ubi} />}
    </>
  );
};

export default Greetings;
